import { Component, Input } from '@angular/core';
import { DowntimeReachabilityImpact } from '../data-access/downtime.models';

@Component({
  selector: 'app-downtime-impact-summary',
  standalone: true,
  template: `
    <div class="downtime-impact-summary">
      <div class="downtime-impact-summary__title">
        Downtime impact summary
      </div>

      <div class="downtime-impact-summary__value">
        Affected appointments: {{ affectedAppointmentIds.length }}
      </div>

      @if (reachabilityImpact) {
        <div class="downtime-impact-summary__grid">
          <div>
            <span>Clients</span>
            <strong>{{ reachabilityImpact.affectedClientsCount }}</strong>
          </div>
          <div class="downtime-impact-summary__automatic">
            <span>Automatic</span>
            <strong>{{ reachabilityImpact.automaticallyReachableClientsCount }}</strong>
            <small>
              Email {{ reachabilityImpact.automaticEmailClientsCount }} ·
              Telegram {{ reachabilityImpact.automaticTelegramClientsCount }}
            </small>
          </div>
          <div class="downtime-impact-summary__manual">
            <span>Manual follow-up</span>
            <strong>{{ reachabilityImpact.manualFollowUpClientsCount }}</strong>
            <small>Phone only {{ reachabilityImpact.manualPhoneOnlyClientsCount }}</small>
          </div>
          <div class="downtime-impact-summary__risk">
            <span>No channel</span>
            <strong>{{ reachabilityImpact.noChannelClientsCount }}</strong>
          </div>
        </div>
      }

      @if (affectedAppointmentIds.length > 0) {
        <div class="downtime-impact-summary__list">
          @for (appointmentId of affectedAppointmentIds; track appointmentId) {
            <div [title]="appointmentId">{{ readableId(appointmentId) }}</div>
          }
        </div>
      }
    </div>
  `,
  styles: [`
    .downtime-impact-summary {
      margin-bottom: 16px;
      padding: 14px 16px;
      border: 1px solid #fde68a;
      background: #fffbeb;
      border-radius: 12px;
    }

    .downtime-impact-summary__title {
      font-size: 14px;
      font-weight: 700;
      color: #92400e;
      margin-bottom: 8px;
    }

    .downtime-impact-summary__value {
      color: #78350f;
      font-size: 14px;
    }

    .downtime-impact-summary__grid {
      display: grid;
      grid-template-columns: repeat(4, minmax(0, 1fr));
      gap: 8px;
      margin-top: 10px;
    }

    .downtime-impact-summary__grid div {
      display: grid;
      gap: 2px;
      min-width: 0;
      padding: 10px;
      border: 1px solid #fde68a;
      border-left-width: 3px;
      border-radius: 8px;
      background: #fff;
      color: #78350f;
    }

    .downtime-impact-summary__grid span {
      font-size: 11px;
      font-weight: 700;
      text-transform: uppercase;
      color: #92400e;
    }

    .downtime-impact-summary__grid strong {
      font-size: 18px;
      line-height: 1.2;
    }

    .downtime-impact-summary__grid small {
      overflow-wrap: anywhere;
      color: #92400e;
      line-height: 1.3;
    }

    .downtime-impact-summary__automatic {
      border-left-color: #22c55e !important;
    }

    .downtime-impact-summary__manual {
      border-left-color: #f59e0b !important;
    }

    .downtime-impact-summary__risk {
      border-left-color: #ef4444 !important;
    }

    .downtime-impact-summary__list {
      margin-top: 8px;
      font-family: monospace;
      font-size: 12px;
      color: #78350f;
    }

    @media (max-width: 760px) {
      .downtime-impact-summary__grid {
        grid-template-columns: 1fr;
      }
    }
  `]
})
export class DowntimeImpactSummaryComponent
{
  @Input()
  public affectedAppointmentIds: string[] = [];

  @Input()
  public reachabilityImpact: DowntimeReachabilityImpact | null = null;

  protected readableId(value: string | null | undefined): string
  {
    const normalized = value?.trim() ?? '';
    if (!normalized) {
      return '—';
    }

    if (normalized.length > 12) {
      return `${normalized.slice(0, 8)}...`;
    }

    return normalized;
  }
}
